import { fetchQuestion, deleteQuestion } from 'Features/Learning/BankQuestions/Utils/QuestionsUtils';
import { handleDeleteConfirmation } from 'Utils/handleDelete';
import { batch } from 'react-redux';

/* **************************************************** */
/* CRUD Materi                                       */
/* **************************************************** */

export const fetchQuestions = (dispatch, filters, refetch) => {
  dispatch(fetchQuestion(filters));
};

export const handleDeleteQuestion = async (dispatch, id, setRefetch) => {
  handleDeleteConfirmation({
    dispatch,
    id,
    setRefetch,
    deleteAction: deleteQuestion,
  });
};

/** Normalisasi flag BE `has_user_answer` (boolean / 0|1). */
export const isQuestionEditLocked = (item) => {
  const flag = item?.has_user_answer;
  return flag === true || flag === 1 || flag === '1';
};

export const handleEditQuestion = async (dispatch, item, setField) => {
  // ✅ Migrasi behavior: clickable & dragpin sekarang dianggap milik fill_in_the_blanks
  // Jadi saat edit data lama (yang mungkin tersimpan sebagai sentence_arrangement), form tetap konsisten.
  const isClickableOrDragpin = item?.interactive_type === 'clickable' || item?.interactive_type === 'dragpin';
  const effectiveQuestionType = isClickableOrDragpin ? 'fill_in_the_blanks' : item?.type;
  const hasUserAnswer = isQuestionEditLocked(item);

  // NOTE: Backend kadang kirim room_count/pin_count sebagai string.
  // DragpinSection menampilkan value hanya kalau tipe-nya number, jadi wajib dinormalisasi.
  const normalizeDragpinCount = (value, fallback = 1, maxValue = 10) => {
    const num = Number(value);
    if (Number.isInteger(num) && num >= 1 && num <= maxValue) return num;
    return fallback;
  };

  // ✅ Field conditional agar tidak "nyangkut" lintas tipe interaktif
  const safeClickableType = item?.interactive_type === 'clickable' ? item?.interactive_detail?.[0]?.clickable_type : '';
  const safeRoomCount = item?.interactive_type === 'dragpin'
    ? normalizeDragpinCount(item?.interactive_detail?.[0]?.room_count ?? item?.room_count, 1, 6)
    : '';
  const safePinCount = item?.interactive_type === 'dragpin'
    ? normalizeDragpinCount(item?.interactive_detail?.[0]?.pin_count ?? item?.pin_count, 1, 10)
    : '';
  const safeStep =
    item?.interactive_type === 'bar_chart' || item?.interactive_type === 'line_chart'
      ? item?.interactive_detail?.[0]?.step
      : '';

  // ✅ Interactive detail: restore dari backend (sequential/dragpin)
  const interactiveDetail = item?.interactive_detail?.[0] ?? {};

  // console.log ("item", item);
  // console.log ("answer explanation", item.answer[0].answer_explanation)

  // ✅ batch() dari react-redux: semua dispatch digabung jadi 1 render.
  // Di React 17, setiap dispatch di async function = render terpisah (tidak di-batch otomatis).
  // Tanpa batch, effects (reset hydration + hydrate) jalan di state campuran →
  // select room berganti-ganti saat navigasi soal.
  batch(() => {
    // PENTING: dispatch id PERTAMA — hydrationKey berubah ke newId|... sejak awal
    // sehingga hydrate tidak terjadi sampai semua field Q-baru sudah masuk.
    dispatch(setField({ field: 'id', value: item.id }));

    // Field global — cukup sekali di sini, bukan di dalam loop
    dispatch(setField({ field: 'is_interactive', value: item.is_interactive }));
    dispatch(setField({ field: 'interactive_type', value: item.interactive_type }));
    dispatch(setField({ field: 'clickable_type', value: safeClickableType }));
    dispatch(setField({ field: 'room_count', value: safeRoomCount }));
    dispatch(setField({ field: 'pin_count', value: safePinCount }));
    dispatch(setField({ field: 'step', value: safeStep }));

    // ✅ Restore interactive_detail (untuk sequential: first_node, second_node)
    if (item?.interactive_type === 'sequential' && interactiveDetail) {
      dispatch(setField({ field: 'interactive_detail', value: interactiveDetail }));
    }

    // ✅ Restore interactive_detail (untuk dragpin: dragpin_pins, room_custom_names, pin_custom_names)
    if (item?.interactive_type === 'dragpin' && interactiveDetail) {
      // Normalize room_custom_names: pastikan array valid
      const roomCustomNames = Array.isArray(interactiveDetail.room_custom_names)
        ? interactiveDetail.room_custom_names
        : [];
      // Normalize pin_custom_names: pastikan array valid
      const pinCustomNames = Array.isArray(interactiveDetail.pin_custom_names)
        ? interactiveDetail.pin_custom_names
        : [];
      // dragpin_pins harus ada untuk restore label/deskripsi per pin
      const dragpinPins = Array.isArray(interactiveDetail.dragpin_pins)
        ? interactiveDetail.dragpin_pins
        : [];

      const detailToRestore = {
        ...interactiveDetail,
        room_custom_names: roomCustomNames,
        pin_custom_names: pinCustomNames,
        dragpin_pins: dragpinPins
      };

      console.log('🔧 Dragpin restore:', {
        item_id: item?.id,
        room_count: safeRoomCount,
        pin_count: safePinCount,
        detailToRestore
      });

      dispatch(setField({
        field: 'interactive_detail',
        value: detailToRestore
      }));
    }

    // Per-field dispatch (title, description, type, answer_explanation, choice, answer)
    const fieldsToUpdate = ['title', 'description', 'type', 'answer_explanation', 'choice', 'answer'];
    fieldsToUpdate.forEach(field => {
      if (field === 'type' || field === 'description' || field === 'title') {
        if (field === 'type') {
          dispatch(setField({ field: 'type', value: effectiveQuestionType }));
        } else {
          dispatch(setField({ field, value: item[field] }));
        }
      } else if (field === 'answer_explanation') {
        dispatch(setField({ field: 'answer_explanation', value: item.answer[0].answer_explanation }));
      } else if (field === 'choice') {
        dispatch(setField({ field: 'choice', value: item.choices.map(choice => choice.choice) }));
      } else if (field === 'answer') {
        if (effectiveQuestionType === 'fill_in_the_blanks' || effectiveQuestionType === 'sentence_arrangement') {
          dispatch(setField({ field: 'answer', value: JSON.parse(item.answer[0].answer) }));
        } else if (item.type === 'multiple_choice') {
          const answerArray = JSON.parse(item.answer[0].answer);
          // Membuat array baru untuk menyimpan hasil pengecekan
          const results = item.choices.map(choice => {
            // Memeriksa apakah id choice ada dalam answerArray
            if (answerArray.includes(String(choice.id))) {
              return "1"; // Jika ada, beri nilai 1
            } else {
              return "0"; // Jika tidak ada, beri nilai 0
            }
          });
          // console.log("results", results);
          dispatch(setField({ field: 'answer', value: results }));
        }
      }
    });

    dispatch(setField({ field: 'answer_explanation_video', value: '' }));
    dispatch(setField({ field: 'audio_source', value: '' }));
    dispatch(setField({ field: 'answer_explanation_videoLink', value: item.answer[0].answer_explanation_video }));
    dispatch(setField({ field: 'audio_sourceLink', value: item.audio_source }));
    dispatch(setField({ field: 'isUpdate', value: true }));
    dispatch(setField({ field: 'has_user_answer', value: hasUserAnswer }));
    dispatch(setField({ field: 'isChoiceUpdate', value: false }));
  });
};

export const handleGetDetailQuestion = async (dispatch, item, setDetail) => {
  dispatch(setDetail(item));
  // console.log("item detail", item);
};

/* **************************************************** */
/* Pagination                                           */
/* **************************************************** */
export const handleBackPagination = (pagination, filters, dispatch, setFilters, setRefetch) => {
  if (pagination.current_page > 1) {
    dispatch(setFilters({
      ...filters,
      page: pagination.current_page - 1
    }));
    setRefetch(prevRefetch => !prevRefetch);
  }
}

export const handleNextPagination = (pagination, filters, dispatch, setFilters, setRefetch) => {
  if (pagination.current_page < pagination.last_page) {
    dispatch(setFilters({
      ...filters,
      page: pagination.current_page + 1
    }));
    setRefetch(prevRefetch => !prevRefetch);
  }
}

export const handleClickPagination = (page, filters, dispatch, setFilters, setRefetch) => {
  dispatch(setFilters({
    ...filters,
    page: page
  }));
  setRefetch(prevRefetch => !prevRefetch);
}